// version v1.4.4.0 (ROUTES FULLY MATCHED TO BACKEND)

import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Badge,
  Spinner,
  Button,
  Modal,
  Pagination
} from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const PAGE_SIZE = 8;
const API_BASE = 'https://apiclientportal.myidealtd.co.uk';

const CustomersPage = () => {
  const { user, authLoaded } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    isAdmin: false
  });

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!authLoaded) return;

      if (!user || !user.isAdmin) {
        setError('Admin access required');
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(
          `${API_BASE}/api/users/admin/customers`,
          { withCredentials: true }
        );

        setCustomers(res.data.users || []);
      } catch (err) {
        setError('Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [authLoaded, user]);

  /* ---------------- FILTER ---------------- */
  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;

    const term = searchTerm.toLowerCase();
    return customers.filter(
      c =>
        c.name.toLowerCase().includes(term) ||
        c.email.toLowerCase().includes(term)
    );
  }, [searchTerm, customers]);

  const totalPages = Math.ceil(filteredCustomers.length / PAGE_SIZE);

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  /* ---------------- MODALS ---------------- */
  const openAddModal = () => {
    setModalType('add');
    setFormData({ name: '', email: '', isAdmin: false });
    setShowModal(true);
  };

  const openEditModal = (c) => {
    setModalType('edit');
    setSelectedCustomer(c);
    setFormData({
      name: c.name,
      email: c.email,
      isAdmin: c.isAdmin
    });
    setShowModal(true);
  };

  const openDeleteModal = (c) => {
    setModalType('delete');
    setSelectedCustomer(c);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
    setFormData({ name: '', email: '', isAdmin: false });
  };

  /* ---------------- ACTIONS ---------------- */
  const handleSave = async () => {
    try {
      if (modalType === 'add') {
        const res = await axios.post(
          `${API_BASE}/api/users/admin/create`,
          formData,
          { withCredentials: true }
        );

        setCustomers(prev => [...prev, res.data.user]);
      }

      if (modalType === 'edit') {
        // ✅ FIXED (MATCH BACKEND)
        const res = await axios.put(
          `${API_BASE}/api/users/admin/${selectedCustomer._id}`,
          formData,
          { withCredentials: true }
        );

        setCustomers(prev =>
          prev.map(c =>
            c._id === selectedCustomer._id ? res.data.user : c
          )
        );
      }

      closeModal();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async () => {
    try {
      // ✅ MATCHES YOUR BACKEND
      await axios.delete(
        `${API_BASE}/api/users/admin/${selectedCustomer._id}`,
        { withCredentials: true }
      );

      setCustomers(prev =>
        prev.filter(c => c._id !== selectedCustomer._id)
      );

      closeModal();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  /* ---------------- STATES ---------------- */
  if (loading)
    return (
      <div className="d-flex justify-content-center vh-100 align-items-center">
        <Spinner />
      </div>
    );

  if (error)
    return <div className="text-center text-danger py-5">{error}</div>;

  /* ---------------- UI ---------------- */
  return (
    <Container fluid className="py-5">
      <Row className="mb-4">
        <Col md={6}>
          <h2>Customers</h2>
        </Col>

        <Col md={6} className="d-flex justify-content-end gap-2">
          <Form.Control
            placeholder="Search..."
            style={{ maxWidth: 250 }}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Button onClick={openAddModal}>+ Add</Button>
        </Col>
      </Row>

      <Table hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Admin</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedCustomers.map(c => (
            <tr key={c._id}>
              <td>{c.name}</td>
              <td>{c.email}</td>
              <td>
                <Badge bg={c.isAdmin ? 'success' : 'secondary'}>
                  {c.isAdmin ? 'Admin' : 'User'}
                </Badge>
              </td>
              <td>
                <Button size="sm" onClick={() => openEditModal(c)}>
                  Edit
                </Button>{' '}
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => openDeleteModal(c)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* MODAL */}
      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalType}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {modalType === 'delete' ? (
            <p>Are you sure you want to delete this user?</p>
          ) : (
            <>
              <Form.Control
                className="mb-2"
                placeholder="Name"
                value={formData.name}
                onChange={e =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <Form.Control
                className="mb-2"
                placeholder="Email"
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
              <Form.Check
                label="Admin"
                checked={formData.isAdmin}
                onChange={e =>
                  setFormData({ ...formData, isAdmin: e.target.checked })
                }
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={closeModal}>Cancel</Button>
          {modalType === 'delete' ? (
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          ) : (
            <Button onClick={handleSave}>Save</Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomersPage;